import { useClientTranslation, useLocaleStore, formatDateTimeFromDate } from '@/lib/core';
import type { TranslationKey } from '@/lib/core/i18n/types';
import type { ReservationSummaryItemDto } from '@/services/platform/api/model';

/**
 * i18n label for each `ReservationSummaryItemDto.key` the API can send.
 *
 * The keys are the backend constants in `ReservationConstants.ReservationSummaryKeys`, emitted
 * per service type by `ReservationSummaryItemsBuilder` (Room → where/checkIn/checkOut/howMany,
 * Driver → where/when/pickup/dropoff, everything else → where/when/howMany + optional
 * allergies/specialOccasion/comment). They arrive as wire strings rather than a closed union, so
 * lookups are lower-cased here to survive a casing change on the other side.
 *
 * `pickup`/`dropoff` deliberately reuse `WhereFrom`/`WhereTo`: those are the words the Range Rover
 * flow already puts on screen for the same two fields, and unlike `App.Labels.Pickup`/`Dropoff`
 * (en-GB only, unreferenced) they are translated in both locales.
 *
 * Typed `Record<string, TranslationKey>`, so a label key that does not exist in the locale files
 * fails the type-check instead of surfacing as raw `App.Labels.Whatever` in the UI.
 */
const SUMMARY_LABEL_KEYS: Record<string, TranslationKey> = {
  where: 'App.Labels.Where',
  when: 'App.Labels.When',
  checkin: 'App.Labels.CheckIn',
  checkout: 'App.Labels.CheckOut',
  howmany: 'App.Labels.HowMany',
  comment: 'App.Labels.Comment',
  allergies: 'App.Labels.Allergies',
  specialoccasion: 'App.Labels.SpecialOccasion',
  pickup: 'App.Labels.WhereFrom',
  dropoff: 'App.Labels.WhereTo',
};

/**
 * The i18n key for a known summary row, or `undefined` when the API sends one we don't map yet.
 *
 * `noUncheckedIndexedAccess` is off, so the index expression types as `TranslationKey` even on a
 * miss — the declared `| undefined` is what forces callers to handle it. Don't narrow it.
 */
export function reservationSummaryLabelKey(key?: string | null): TranslationKey | undefined {
  return key ? SUMMARY_LABEL_KEYS[key.trim().toLowerCase()] : undefined;
}

/**
 * Resolves a summary item's key to its display label: known key → its translation; unknown key →
 * the raw server key, so a summary row the backend adds before the app maps it stays visible (and
 * visibly wrong) rather than silently losing its value. The backend drops empty values before
 * sending, so anything that arrives is meant to be read.
 */
export function useReservationSummaryLabel(): (key?: string | null) => string {
  const t = useClientTranslation();
  return (key?: string | null) => {
    const id = reservationSummaryLabelKey(key);
    return id ? t(id) : (key ?? '');
  };
}

/**
 * Renders a summary item's value for display: the builder sends raw ISO instants on the date rows,
 * a bare integer on `howMany`, and a hard-coded English `"Yes"` on the two flag rows.
 *
 * Deliberately service-type agnostic — the Activity screen is a history view with no flow
 * vocabulary, unlike `BookingDetailSummary`, whose value logic reaches into adults/children counts
 * and status to build Sunbed, Parking and Driver wording.
 */
export function useReservationSummaryValue(): (item: ReservationSummaryItemDto) => string {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);

  return (item: ReservationSummaryItemDto) => {
    const value = item.value ?? '';

    switch (item.key?.trim().toLowerCase()) {
      case 'when':
      case 'checkin':
      case 'checkout':
        // `formatDate` renders "Invalid Date, Invalid Date NaN" rather than throwing, so anything
        // that isn't a real instant is passed through untouched.
        return Number.isNaN(new Date(value).getTime())
          ? value
          : formatDateTimeFromDate(value, locale) || value;

      case 'howmany': {
        // `App.Labels.Guest`/`Guests` are plain nouns, not ICU plurals — hence the manual prefix.
        const count = Number(value);
        return Number.isInteger(count) && count > 0
          ? `${count} ${count === 1 ? t('App.Labels.Guest') : t('App.Labels.Guests')}`
          : value;
      }

      case 'allergies':
      case 'specialoccasion':
        return value.toLowerCase() === 'yes' ? t('App.Labels.Yes') : value;

      default:
        return value;
    }
  };
}
